import { Component, computed, Input, OnInit, signal } from '@angular/core';
import type { MapboxGeoJSONFeature } from 'mapbox-gl';
import {
  ARBITRARY_CELL,
  COUNTY_LEVEL_LAYER,
  STATE_LEVEL_LAYER,
} from '../../../services/layer-store.service';
import {
  DEFAULT_TRADE_AREA_SHAPE,
  DRIVE_TIME_PRESETS,
  isSamePoint,
  isSameTradeArea,
  LngLatPair,
  RADIUS_PRESETS,
  TradeAreaKind,
  TradeAreaService,
  TradeAreaShape,
} from '../../../services/trade-area.service';
import { PopupService } from '../../../services/popup.service';
import { SearchGeocoderService } from '../../../../../shared/services/search-geocoder.service';
import { MapBoxService } from '../../../mapbox.service';
import { UserAccessService } from '../../../../../user/access/user-access.service';
import { ModalService } from '../../../../../shared/services/modal.service';
import { FeatureTooltipComponent } from '../../../../menu/right-menu/layers-menu/shared/feature-tooltip/feature-tooltip.component';
import { MapModalData } from '../../../../../user/map-redirect-modal/map-redirect-modal.component';
import { LockIconComponent } from '../../../../../shared/components/lock-icon/lock-icon.component';

/**
 * Deep enough that the level logic hands the click to the ~1 sq mi hexagons
 * whatever level was picked before (county holds until 11), so the point the
 * visitor picks is one they can actually see.
 */
const PICK_SPOT_ZOOM = 13;

/**
 * The (i) hint. Split on "Source:" by FeatureTooltipComponent into the
 * modal's body and source line, like every indicator hint in the app.
 */
export const TRADE_AREA_HINT =
  'The area around the pin that your customers come from. A drive time ' +
  'follows real roads at typical weekday midday traffic; a radius is a ' +
  'straight-line circle. Every number in the card then describes that ' +
  'whole area, not a single cell. ' +
  'Source: drive times from the Mapbox Isochrone API.';

/** Reported with `sign_up` when this gate made a guest create an account. */
const TRADE_AREA_AUTH_PLACEMENT = 'map-trade-area';

/**
 * - `point`: a local cell - analyze around the spot that was clicked.
 * - `area`: a state or county, where a click is too coarse to be a location.
 * - `analyzed`: the popup of an area already analyzed - change or clear it.
 */
type Mode = 'point' | 'area' | 'analyzed';

/**
 * The popup's trade-area row: a drive-time or radius area around the clicked
 * spot, analyzed with the same popup (and the same plan-based padlocks) as
 * any cell. Signed-in visitors only; guests get the sign-up gate on Analyze.
 */
@Component({
  selector: 'app-trade-area-action',
  templateUrl: './trade-area-action.component.html',
  styleUrls: ['./trade-area-action.component.scss'],
  imports: [FeatureTooltipComponent, LockIconComponent],
})
export class TradeAreaActionComponent implements OnInit {
  @Input({ required: true }) feature!: MapboxGeoJSONFeature;

  public mode: Mode = 'point';

  /** State/county popup opened by searching that area, not by a click. */
  public isAreaSearch = false;

  public readonly hint = TRADE_AREA_HINT;
  public readonly drivePresets = DRIVE_TIME_PRESETS;
  public readonly radiusPresets = RADIUS_PRESETS;

  public readonly shape = signal<TradeAreaShape>(DEFAULT_TRADE_AREA_SHAPE);

  private center: LngLatPair | null = null;

  public readonly isAnalyzing = computed(() => {
    const pending = this.tradeArea.pending();
    return (
      !!pending && !!this.center && isSamePoint(pending.center, this.center)
    );
  });

  /** In an analyzed popup, whether the picked shape is the one on the map. */
  public readonly isUnchanged = computed(
    () =>
      this.mode === 'analyzed' &&
      !!this.center &&
      isSameTradeArea(this.tradeArea.analyzed(), {
        center: this.center,
        shape: this.shape(),
      })
  );

  constructor(
    private tradeArea: TradeAreaService,
    private popupService: PopupService,
    private geocoder: SearchGeocoderService,
    private mapboxService: MapBoxService,
    private modalService: ModalService,
    public accessService: UserAccessService
  ) {}

  ngOnInit(): void {
    const level = this.feature.layer.id;
    this.shape.set(this.tradeArea.lastShape);

    if (level === ARBITRARY_CELL) {
      // The popup opens wherever the area was clicked, but the area is
      // anchored to the spot it was analyzed around.
      const analyzed = this.tradeArea.analyzed();
      this.mode = 'analyzed';
      this.center = analyzed?.center ?? null;
      if (analyzed) this.shape.set(analyzed.shape);
      return;
    }

    const { lng, lat } = this.popupService.popup.getLngLat();
    this.center = [lng, lat];

    if (level === STATE_LEVEL_LAYER || level === COUNTY_LEVEL_LAYER) {
      this.mode = 'area';
      this.isAreaSearch = this.geocoder.isAreaSearchPoint(this.center);
    }
  }

  public shapeKey(kind: TradeAreaKind, value: number): string {
    return `${kind}:${value}`;
  }

  public onShapeChange(event: Event): void {
    const [kind, value] = (event.target as HTMLSelectElement).value.split(':');
    this.shape.set({ kind: kind as TradeAreaKind, value: Number(value) });
  }

  public openHint(modalData: MapModalData): void {
    this.modalService.openModal(modalData);
  }

  public async analyze(): Promise<void> {
    if (!this.center) return;

    if (this.accessService.isUnauthorized) {
      const signedIn = await this.modalService.requestAuth({
        headerText: 'Trade area analysis is free with an account',
        subHeaderText:
          'Signing up takes a few seconds - no credit card, nothing to pay.',
        placement: TRADE_AREA_AUTH_PLACEMENT,
      });
      if (!signedIn) return;
    }

    this.tradeArea.analyze({ center: this.center, shape: this.shape() });
  }

  public clear(): void {
    this.tradeArea.clear();
    this.popupService.handleClose();
  }

  /**
   * Takes the visitor to street level at the spot they clicked and opens the
   * local popup there, where this row offers the analysis itself.
   */
  public pickSpot(): void {
    if (!this.center) return;

    this.geocoder.flyToAddress(
      this.center,
      this.mapboxService.map,
      PICK_SPOT_ZOOM
    );
  }
}
